import { FC } from 'react'
import { PureQueryOptions } from '@apollo/client'
import gql from 'graphql-tag'
import styled, { css } from 'styled-components'
import {
  CoverLetterListItem_CurrentUserFragment,
  CoverLetterParagraphScrapListItem_CoverLetterParagraphScrapFragment,
} from 'generated/graphql'
import Link, { LinkProps } from 'components/Link'
import CircularProgress from 'svgIcons/CircularProgress'
import Close from 'svgIcons/Close'
import { getPassedAtText } from 'utils/coverLetter'
import CoverLetterParagraphScrapDelete from './CoverLetterParagraphScrapDelete'

interface CoverLetterParagraphScrapListItemProps {
  currentUser?: CoverLetterListItem_CurrentUserFragment | null
  coverLetterParagraphScrap: CoverLetterParagraphScrapListItem_CoverLetterParagraphScrapFragment
  scrapRefetchQueries?: PureQueryOptions[]
  LinkProps?: LinkProps
}

const CoverLetterParagraphScrapListItem: FC<
  CoverLetterParagraphScrapListItemProps
> = ({
  currentUser,
  coverLetterParagraphScrap,
  scrapRefetchQueries,
  LinkProps = {
    href: '/',
    naked: true,
  },
}) => {
  const { paragraph, coverLetter } = coverLetterParagraphScrap

  return (
    <StyledWrapper>
      <Link className="link" {...LinkProps}>
        <div className="cover-letter-infos">
          <p className="passed-at">{getPassedAtText(coverLetter)}</p>
          <p className="ellipsis organization-name">
            {coverLetter?.organizationName}
          </p>
          <p className="ellipsis role">{coverLetter?.role}</p>
        </div>
        <div className="content-wrapper">
          <span className="paragraph-scrap-badge">문장 스크랩</span>
          <h4 className="content">{paragraph}</h4>
        </div>
      </Link>
      <div className="close-icon-wrapper">
        <CoverLetterParagraphScrapDelete
          coverLetterParagraphScrap={coverLetterParagraphScrap}
          currentUser={currentUser}
          refetchQueries={scrapRefetchQueries}
        >
          {({ deleteCoverLetterParagraphScrap, loading }) => (
            <button
              onClick={deleteCoverLetterParagraphScrap}
              disabled={loading}
            >
              {!loading ? (
                <Close className="close-icon" />
              ) : (
                <CircularProgress width={18} height={18} color="#01a0ff" />
              )}
            </button>
          )}
        </CoverLetterParagraphScrapDelete>
      </div>
    </StyledWrapper>
  )
}

export default CoverLetterParagraphScrapListItem

gql`
  fragment CoverLetterParagraphScrapListItem_coverLetterParagraphScrap on CoverLetterParagraphScrap {
    id
    paragraph
    coverLetter {
      id
      organizationName
      role
      passedAt
    }
  }
`

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    padding: 20px 0;
    border-bottom: 1px solid ${theme.line.line_3};

    @media (max-width: 600px) {
      padding: 16px 0;
    }

    .link {
      display: flex;
      align-items: center;
      width: 100%;
      font-size: 16px;
      font-weight: normal;
      &:hover {
        text-decoration: underline;
      }
    }

    .cover-letter-infos {
      display: flex;
      @media (max-width: 600px) {
        display: none;
      }
    }

    .content {
      font-size: 12px;
      font-weight: normal;
      color: ${theme.color.text_3};

      @media (min-width: 601px) {
        font-size: 16px;
        color: inherit;
      }
    }

    .passed-at {
      margin-left: 10px;
      margin-right: 20px;
      width: 100px;
      text-align: center;
    }

    .organization-name {
      margin-right: 20px;
      text-align: center;
      width: 100px;
    }

    .role {
      margin-right: 36px;
      text-align: center;
      width: 100px;
    }

    .content-wrapper {
      margin-right: 32px;
      flex-grow: 1;
      display: block;
      min-width: 0;
      flex-direction: row;
      letter-spacing: -0.64px;
    }

    .paragraph-scrap-badge {
      align-self: flex-start;
      font-size: 12px;
      font-weight: bold;
      padding: 4px 12px;
      border-radius: 5px;
      margin-bottom: 5px;
      background-color: ${theme.background.bg_1};
      display: inline-flex;
      align-items: center;
      height: 30px;

      @media (max-width: 600px) {
        height: 20px;
      }
    }

    .ellipsis {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    .close-icon-wrapper {
      padding-bottom: 5px;
      text-align: center;
      margin-right: 10px;

      @media (max-width: 600px) {
        padding-left: 20px;
        margin-right: 0;
      }

      button {
        padding: 12px;
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }
    }
  `}
`
